import { useItem } from '@/composables/item'
import { documentService } from '@/services/api'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { useAlertStore } from '../system'
import { useDocumentContainerStore } from './documentContainer'
import { ref } from 'vue'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import dayjs from 'dayjs'
import { makeUniqueZipEntryName } from '@/helpers/uniqueFilename'

type Status = 'initial' | 'ready' | 'loading'

export const useDocumentStore = defineStore('document', () => {
	const { items, show, create, getItem, remove } =
		useItem<Api.DocumentResource>(documentService, 'documents', 'Document', {
			reloadItemOnRemove: [{ id: 'container_id', store: 'documentcontainer' }],
		})

	const status = ref('')

	function statusSet(value: Status) {
		status.value = value
	}
	function thumbnailAdd({ id, page, thumbnail }) {
		const item = items.value.find((item) => item.id === parseInt(id))
		if (item) {
			if (typeof item.thumbnails === 'undefined') {
				item.thumbnails = []
			}
			item.thumbnails[page] = thumbnail
		}
	}

	function getThumbnail({ id, page }) {
		const item = items.value.find((item) => item.id === parseInt(id))
		return Array.isArray(item?.thumbnails)
			? items.value.find((item) => item.id === parseInt(id))?.thumbnails[page]
			: undefined
	}

	async function loadFile(file: Api.Document, endpoint?: string) {
		const name = documentName({ id: file.id, file: file })
		return await documentBinary({ id: file.id, endpoint }).then(({ data }) => {
			const fileObject = new File([data], name, {
				type: data.type,
			})
			return { name, file: fileObject, meta: file }
		})
	}

	async function loadFiles(items: Api.Document[], endpoint?: string) {
		try {
			return await Promise.all(
				items.map(async (file) => loadFile(file, endpoint))
			)
		} catch (error) {
			console.error('failed to load files', error)
			throw error
		}
	}

	async function saveFile(item: Api.Document, endpoint?: string) {
		const file = await loadFile(item, endpoint)
		saveAs(file.file, file.name)
	}

	async function saveFilesAsZip(items: Api.Document[], endpoint?: string) {
		try {
			const files = await loadFiles(items, endpoint)
			if (!files) return

			const zip = new JSZip()
			for (const file of files) {
				if (file.file === null) continue
				// Gleichnamige Dokumente wuerden sich sonst gegenseitig im Archiv ersetzen.
				zip.file(
					makeUniqueZipEntryName(
						file.name,
						(candidate) => zip.file(candidate) !== null
					),
					file.file
				)
			}

			const blob = await zip.generateAsync({ type: 'blob' })
			saveAs(blob, `Dateien-${dayjs().format('DD.MM.YYYY HH:mm')}.zip`)
		} catch (error) {
			console.error(error)
			throw error
		}
	}

	function get(id: number) {
		return documentService.binary(id, 'blob').then(
			(binary) => {
				return binary
			},
			(error) => {
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	async function documentBinary({
		id,
		responseType = 'blob',
		endpoint,
		onDownloadProgress,
		signal,
	}: {
		id: number
		responseType?: Api.ResponseType
		endpoint?: string
		onDownloadProgress?: (e: import('axios').AxiosProgressEvent) => void
		signal?: AbortSignal
	}) {
		return await documentService.binary<Blob>(
			id,
			responseType,
			endpoint,
			onDownloadProgress,
			signal
		)
	}
	function documentName({ id, file }) {
		file = file ?? getItem(id)
		let name
		if (!Object.prototype.hasOwnProperty.call(file, 'document_container')) {
			file.document_container = useDocumentContainerStore().show(
				file.container_id
			)
		}
		name =
			file.document_container.document_files_count === 1
				? file.document_container.comment
				: file.file_notes
		const fileType = file.file_original_name.substr(
			file.file_original_name.lastIndexOf('.')
		)
		if (name.indexOf(fileType) === -1) {
			name += fileType
		}
		// eslint-disable-next-line no-useless-escape
		return name.replace(/[\\\/:*?"<>|]/g, '-')
	}

	function thumbnailLoad({ id, page }) {
		const thumbnail = getThumbnail({ id, page })
		if (typeof thumbnail !== 'undefined' && thumbnail.byteLength > 0) {
			return thumbnail
		}
		statusSet('loading')
		return documentService.thumbnail(id, page + 1).then((thumbnail) => {
			thumbnailAdd({ id, page, thumbnail })
			statusSet('ready')
			return thumbnail
		})
	}

	const showWhat = ({ file_hash_name }: Api.Document) => {
		const type = /(?:\.([^.]+))?$/.exec(file_hash_name)[1]
		if (['bmp', 'jpeg', 'jpg', 'png'].includes(type)) {
			return 'img'
		} else {
			if (['pdf'].includes(type)) {
				// show PdfViewer
				return 'pdf'
			} else {
				// show Dialog with Preview
				return ''
			}
		}
	}

	return {
		items,
		show,
		create,
		remove,
		statusSet,
		thumbnailAdd,
		getThumbnail,
		get,
		documentBinary,
		documentName,
		thumbnailLoad,
		getItem,
		loadFile,
		loadFiles,
		saveFilesAsZip,
		saveFile,
		showWhat,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useDocumentStore, import.meta.hot))
}
